import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Shield,
  Download,
  FileText,
  Mail,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Lock,
  Database,
  Globe,
} from "lucide-react";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gdprInfo, setGdprInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchGdprInfo();
  }, []);

  const fetchGdprInfo = async () => {
    try {
      const response = await axios.get(`${API}/privacy/gdpr`);
      setGdprInfo(response.data);
    } catch (error) {
      toast.error("Failed to load privacy policy");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/privacy/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "explorio_privacy_policy.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Privacy policy downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleEmailPolicy = async () => {
    if (!user?.email) {
      toast.error("Please log in to receive the policy via email");
      return;
    }

    try {
      await axios.post(
        `${API}/privacy/email`,
        { email: user.email },
        { withCredentials: true }
      );
      toast.success(`Privacy policy sent to ${user.email}`);
    } catch (error) {
      toast.error("Failed to send email");
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container p-4 pb-28" data-testid="privacy-policy-page">
      <SEO title="Privacy Policy" description="Explorio's privacy policy. Learn how we handle your data, GDPR rights, and account deletion." path="/privacy" />
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Privacy Policy</h1>
            <p className="text-sm text-slate-400">GDPR Compliance</p>
          </div>
        </div>
      </div>

      {/* Download Actions */}
      <div className="flex gap-3 mb-6">
        <Button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="flex-1 btn-primary flex items-center justify-center gap-2"
          data-testid="download-pdf-button"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Download PDF
        </Button>
        <Button
          onClick={handleEmailPolicy}
          variant="outline"
          className="flex-1 btn-outline flex items-center justify-center gap-2"
          data-testid="email-policy-button"
        >
          <Mail className="w-4 h-4" />
          Email to Me
        </Button>
      </div>

      {/* Policy Content */}
      <ScrollArea className="h-[calc(100vh-280px)]">
        {gdprInfo && (
          <div className="space-y-6 pr-4">
            {/* Introduction */}
            <section className="glass-card p-4">
              <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-400" />
                {gdprInfo.title}
              </h2>
              <p className="text-sm text-slate-400">
                Last updated: {gdprInfo.last_updated}
              </p>
              <p className="text-sm text-slate-300 mt-3">
                At Explorio, we take your privacy seriously. This policy
                explains how we collect, use, and protect your personal data in compliance
                with the General Data Protection Regulation (GDPR).
              </p>
            </section>

            {/* Data We Collect */}
            <section className="glass-card p-4">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-sky-400" />
                Data We Collect
              </h3>
              <ul className="space-y-2">
                {gdprInfo.data_collected?.map((item, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-300 flex items-start gap-2"
                  >
                    <span className="text-sky-400 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* How We Use Data */}
            <section className="glass-card p-4">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-400" />
                How We Use Your Data
              </h3>
              <ul className="space-y-2">
                {gdprInfo.data_usage?.map((item, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-300 flex items-start gap-2"
                  >
                    <span className="text-emerald-400 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Your Rights */}
            <section className="glass-card p-4">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Your Rights Under GDPR
              </h3>
              <ul className="space-y-2">
                {gdprInfo.your_rights?.map((item, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-300 flex items-start gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Security Measures */}
            <section className="glass-card p-4">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-400" />
                Security Measures
              </h3>
              <ul className="space-y-2">
                {gdprInfo.security_measures?.map((item, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-slate-300 flex items-start gap-2"
                  >
                    <span className="text-amber-400 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Data Retention */}
            <section className="glass-card p-4">
              <h3 className="font-semibold text-white mb-3">Data Retention</h3>
              <p className="text-sm text-slate-300">{gdprInfo.data_retention}</p>
            </section>

            {/* Contact */}
            <section className="glass-card p-4">
              <h3 className="font-semibold text-white mb-3">Contact Us</h3>
              <p className="text-sm text-slate-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-violet-400" />
                {gdprInfo.contact}
              </p>
              <p className="text-sm text-slate-400 mt-2">
                Data Controller: {gdprInfo.data_controller}
              </p>
            </section>

            {/* Exercise Your Rights */}
            <section className="glass-card p-4 border border-violet-500/30">
              <h3 className="font-semibold text-white mb-3">Exercise Your Rights</h3>
              <p className="text-sm text-slate-300 mb-4">
                You can manage your data directly from your Personal settings:
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate("/personal")}
                  className="btn-primary flex items-center gap-2"
                  data-testid="go-to-personal"
                >
                  <Shield className="w-4 h-4" />
                  Manage My Data
                </Button>
              </div>
            </section>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default PrivacyPolicy;
